"use client";

import { useRef, useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "@/components/icons";
import { cn } from "@/lib/utils";
import { scrollByMotionSafe } from "@/lib/motion";
import { IconButton } from "@/components/ui/icon-button";

interface ScrollableRowProps {
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  showIndicatorsOnDesktop?: boolean;
  arrowPlacement?: ArrowPlacement;
}

type ArrowPlacement = "inside" | "outside" | "edge" | "flush";

function useScrollableEdges() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    checkScroll();

    el.addEventListener("scroll", checkScroll, { passive: true });
    window.addEventListener("resize", checkScroll);

    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
      resizeObserver.disconnect();
    };
  }, [checkScroll]);

  const scroll = useCallback((direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const step = Math.max(el.clientWidth * 0.8, 1);
    scrollByMotionSafe(el, {
      left: direction === "left" ? -step : step,
    });
  }, []);

  return { scrollRef, canScrollLeft, canScrollRight, scroll };
}

export function ScrollableRow({
  children,
  className,
  contentClassName,
  showIndicatorsOnDesktop = false,
  arrowPlacement = "inside",
}: ScrollableRowProps) {
  const { scrollRef, canScrollLeft, canScrollRight, scroll } =
    useScrollableEdges();
  const indicatorVisibility = showIndicatorsOnDesktop ? "" : "md:hidden";

  return (
    <div className={cn("group relative group/scroll", className)}>
      {canScrollLeft && (
        <RailEdge
          direction="left"
          placement={arrowPlacement}
          visibility={indicatorVisibility}
          onScroll={scroll}
        />
      )}

      <div
        ref={scrollRef}
        className={cn(
          "overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          "-webkit-overflow-scrolling-touch",
          contentClassName,
        )}
      >
        {children}
      </div>

      {canScrollRight && (
        <RailEdge
          direction="right"
          placement={arrowPlacement}
          visibility={indicatorVisibility}
          onScroll={scroll}
        />
      )}
    </div>
  );
}

function RailEdge({
  direction,
  placement,
  visibility,
  onScroll,
}: {
  direction: "left" | "right";
  placement: ArrowPlacement;
  visibility: string;
  onScroll: (direction: "left" | "right") => void;
}) {
  return (
    <>
      <EdgeFade
        direction={direction}
        placement={placement}
        visibility={visibility}
      />
      <ScrollArrow
        direction={direction}
        placement={placement}
        onClick={() => onScroll(direction)}
      />
    </>
  );
}

function EdgeFade({
  direction,
  placement,
  visibility,
}: {
  direction: "left" | "right";
  placement: ArrowPlacement;
  visibility: string;
}) {
  if (placement !== "inside") return null;
  return (
    <div
      aria-hidden="true"
      className={cn(
        "absolute top-0 bottom-0 w-10 z-10 pointer-events-none",
        direction === "left"
          ? "left-0 bg-gradient-to-r from-background to-transparent"
          : "right-0 bg-gradient-to-l from-background to-transparent",
        visibility,
      )}
    />
  );
}

function ScrollArrow({
  direction,
  placement,
  onClick,
}: {
  direction: "left" | "right";
  placement: ArrowPlacement;
  onClick: () => void;
}) {
  const Icon = direction === "left" ? ChevronLeft : ChevronRight;
  const outside = placement === "outside";
  const edge = placement === "edge";
  const flush = placement === "flush";

  return (
    <IconButton
      variant="outline"
      size={edge || flush ? "default" : "compact"}
      onClick={onClick}
      aria-label={`Scroll ${direction}`}
      className={cn(
        "absolute top-1/2 z-20 hidden -translate-y-1/2 rounded-full shadow-sm",
        placement === "inside"
          ? "reveal-on-hover transition-opacity md:inline-flex"
          : "md:inline-flex",
        direction === "left" ? "left-2" : "right-2",
        flush && "size-12 hover:border-brand hover:bg-brand hover:text-brand-foreground",
        flush && (direction === "left" ? "lg:left-4" : "lg:right-4"),
        edge && (direction === "left" ? "lg:-left-4" : "lg:-right-4"),
        outside &&
          (direction === "left"
            ? "min-[1320px]:-left-[50px]"
            : "min-[1320px]:-right-[50px]"),
      )}
    >
      <Icon className={flush ? "size-6" : undefined} />
    </IconButton>
  );
}
